const LOOKBACK_OFFSETS = [1, 5, 9];
const NEARBY_INTERVAL_FACTOR = 2;

export function getStabilityFilterThreshold(siloSettings) {
  const threshold = siloSettings?.[0]?.settings?.frontend_sequential_stability_filter_threshold;
  return threshold != null ? Number(threshold) : 5;
}

function profileTime(profile) {
  const time = new Date(profile?.datetime_fetched).getTime();
  return Number.isFinite(time) ? time : null;
}

function medianSampleInterval(newestFirst) {
  const deltas = [];
  for (let index = 1; index < newestFirst.length; index += 1) {
    const newer = profileTime(newestFirst[index - 1]);
    const older = profileTime(newestFirst[index]);
    if (newer == null || older == null) continue;
    const delta = newer - older;
    if (delta > 0) deltas.push(delta);
  }
  if (!deltas.length) return null;
  deltas.sort((a, b) => a - b);
  return deltas[Math.floor((deltas.length - 1) / 2)];
}

function newerReferenceIsNearby(reference, profile, offset, medianInterval) {
  if (!reference || medianInterval == null) return false;
  const current = profileTime(profile);
  const newer = profileTime(reference);
  if (current == null || newer == null || newer < current) return false;
  return newer - current <= offset * medianInterval * NEARBY_INTERVAL_FACTOR;
}

export function filterStableSiloProfiles(profiles, threshold = 5) {
  if (!profiles || !Array.isArray(profiles) || profiles.length === 0) {
    return [];
  }

  const newestFirst = [...profiles].sort(
    (a, b) => new Date(b.datetime_fetched) - new Date(a.datetime_fetched)
  );
  const medianInterval = medianSampleInterval(newestFirst);
  const limit = parseInt(threshold, 10);

  const stableNewestFirst = newestFirst.reduce((acc, profile, idx, array) => {
    if (profile.level == null || profile.level <= 0) {
      return acc;
    }

    const references = LOOKBACK_OFFSETS.map(offset => array[idx - offset]);
    const hasDistantOrMissingReference = references.some((reference, index) => (
      reference == null || !newerReferenceIsNearby(reference, profile, LOOKBACK_OFFSETS[index], medianInterval)
    ));
    if (hasDistantOrMissingReference) {
      acc.push(profile);
      return acc;
    }

    const isStable = references.every(
      reference => Math.abs(profile.level - reference.level) < limit
    );
    if (!isStable) {
      return acc;
    }

    acc.push(profile);
    return acc;
  }, []);

  return stableNewestFirst.sort(
    (a, b) => new Date(a.datetime_fetched) - new Date(b.datetime_fetched)
  );
}

export function getLastStableSiloProfile(profiles, threshold = 5) {
  const filtered = filterStableSiloProfiles(profiles, threshold);
  return filtered.length ? filtered[filtered.length - 1] : null;
}

export function applyStableProfileMetrics(baseProfile, stableProfile) {
  if (!baseProfile) return null;
  if (!stableProfile) return baseProfile;

  return {
    ...baseProfile,
    level: stableProfile.level ?? baseProfile.level,
    weight: stableProfile.weight ?? baseProfile.weight,
    volume: stableProfile.volume ?? baseProfile.volume,
    food: stableProfile.food ?? baseProfile.food,
    last_updated: stableProfile.datetime_fetched ?? stableProfile.last_updated ?? baseProfile.last_updated,
    datetime_fetched: stableProfile.datetime_fetched ?? baseProfile.datetime_fetched,
  };
}
